import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
/** Abaixo de `CAMADA_MODAL` (200, em `Modal.tsx`): o painel não pode
 *  competir com um diálogo aberto por cima da tela. */
const CAMADA_PAINEL = 50;
function semAcento(s) {
    return s
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLowerCase();
}
export function FiltroPill({ label, opcoes, selecionados, onChange, limiarBusca = 8, buscaPlaceholder = "Buscar...", alinhamento = "esquerda", carregando = false, }) {
    const [open, setOpen] = useState(false);
    const [busca, setBusca] = useState("");
    const [destaque, setDestaque] = useState(0);
    const [ladoDireita, setLadoDireita] = useState(alinhamento === "direita");
    const wrapperRef = useRef(null);
    const painelRef = useRef(null);
    const gatilhoRef = useRef(null);
    const buscaRef = useRef(null);
    const idPainel = useId();
    const temBusca = opcoes.length > limiarBusca;
    const opcoesFiltradas = useMemo(() => {
        if (!temBusca || !busca)
            return opcoes;
        const alvo = semAcento(busca);
        return opcoes.filter((o) => semAcento(o.label).includes(alvo));
    }, [opcoes, busca, temBusca]);
    // Fecha ao clicar fora (mousedown, nao click — arrasto que comeca dentro e
    // termina fora nao deve fechar) ou por Esc, que devolve o foco ao gatilho.
    useEffect(() => {
        if (!open)
            return;
        function aoClicarFora(e) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target))
                setOpen(false);
        }
        function aoTeclar(e) {
            if (e.key === "Escape") {
                e.stopPropagation();
                setOpen(false);
                gatilhoRef.current?.focus();
            }
        }
        document.addEventListener("mousedown", aoClicarFora);
        document.addEventListener("keydown", aoTeclar);
        return () => {
            document.removeEventListener("mousedown", aoClicarFora);
            document.removeEventListener("keydown", aoTeclar);
        };
    }, [open]);
    // Ao abrir: foca a busca (se houver) e mede o espaco disponivel pra decidir
    // o lado do painel — a pill mais a direita da barra estouraria a janela se
    // sempre abrisse pra esquerda.
    useLayoutEffect(() => {
        if (!open)
            return;
        setBusca("");
        setDestaque(0);
        if (temBusca)
            buscaRef.current?.focus();
        const rect = wrapperRef.current?.getBoundingClientRect();
        const largura = painelRef.current?.offsetWidth ?? 260;
        if (rect)
            setLadoDireita(rect.left + largura > window.innerWidth - 8);
    }, [open, temBusca]);
    function alternar(value) {
        if (selecionados.includes(value))
            onChange(selecionados.filter((v) => v !== value));
        else
            onChange([...selecionados, value]);
    }
    // So' existe quando ha campo de busca: o foco fica no input pra continuar
    // digitando, entao a navegacao e' um destaque VIRTUAL (aria-activedescendant),
    // nao foco DOM real. Sem busca os proprios checkboxes ja tem Tab nativo.
    function aoTeclarNaBusca(e) {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setDestaque((i) => Math.min(i + 1, opcoesFiltradas.length - 1));
        }
        else if (e.key === "ArrowUp") {
            e.preventDefault();
            setDestaque((i) => Math.max(i - 1, 0));
        }
        else if (e.key === "Enter") {
            e.preventDefault();
            const alvo = opcoesFiltradas[destaque];
            if (alvo)
                alternar(alvo.value);
        }
    }
    const rotulo = selecionados.length === 0
        ? label
        : selecionados.length === 1
            ? `${label}: ${opcoes.find((o) => o.value === selecionados[0])?.label ?? selecionados[0]}`
            : `${label}: ${selecionados.length} selecionados`;
    const ativo = selecionados.length > 0;
    return (_jsxs("div", { ref: wrapperRef, style: { position: "relative", display: "inline-flex" }, children: [_jsxs("span", { style: {
                    display: "inline-flex",
                    alignItems: "center",
                    borderRadius: 999,
                    border: `1px solid ${ativo ? "var(--accent)" : "var(--border)"}`,
                    background: ativo ? "color-mix(in srgb, var(--accent) 16%, transparent)" : "var(--surface)",
                    fontSize: 12.5,
                }, children: [_jsxs("button", { ref: gatilhoRef, type: "button", "aria-haspopup": "listbox", "aria-expanded": open, "aria-controls": idPainel, onClick: () => setOpen((v) => !v), style: {
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "5px 11px",
                            border: "none",
                            background: "transparent",
                            color: ativo ? "var(--text)" : "var(--text-muted)",
                            fontWeight: ativo ? 700 : 500,
                            fontSize: "inherit",
                            fontFamily: "inherit",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                        }, children: [_jsx("span", { style: { maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }, title: rotulo, children: rotulo }), _jsx("span", { "aria-hidden": "true", style: { fontSize: ".75em", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }, children: "\u25BE" })] }), ativo ? (_jsx("button", { type: "button", "aria-label": `Limpar filtro de ${label}`, onClick: () => onChange([]), style: {
                            padding: "5px 9px 5px 0",
                            border: "none",
                            background: "transparent",
                            color: "var(--text)",
                            cursor: "pointer",
                            fontSize: "inherit",
                            lineHeight: 1,
                        }, children: "\u2715" })) : null] }), open ? (_jsxs("div", { ref: painelRef, id: idPainel, role: "listbox", "aria-multiselectable": "true", style: {
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    ...(ladoDireita ? { right: 0 } : { left: 0 }),
                    minWidth: 220,
                    width: "max-content",
                    maxWidth: 320,
                    background: "var(--bg-elev)",
                    border: "1px solid var(--border-strong)",
                    borderRadius: 12,
                    boxShadow: "0 12px 32px rgba(0,0,0,.45)",
                    padding: 8,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    zIndex: CAMADA_PAINEL,
                }, children: [temBusca ? (_jsx("input", { ref: buscaRef, type: "search", value: busca, onChange: (e) => {
                            setBusca(e.target.value);
                            setDestaque(0);
                        }, onKeyDown: aoTeclarNaBusca, placeholder: buscaPlaceholder, role: "combobox", "aria-expanded": "true", "aria-controls": idPainel, "aria-activedescendant": opcoesFiltradas[destaque] ? `${idPainel}-${opcoesFiltradas[destaque].value}` : undefined, style: {
                            padding: "7px 10px",
                            borderRadius: 8,
                            border: "1px solid var(--border-strong)",
                            background: "var(--bg-elev-2)",
                            color: "var(--text)",
                            fontSize: 13,
                            outline: "none",
                            marginBottom: 4,
                        } })) : null, _jsx("div", { style: { maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }, children: carregando ? (_jsx("div", { style: { padding: "8px 10px", fontSize: 13, color: "var(--text-dim)" }, children: "Carregando\u2026" })) : opcoesFiltradas.length === 0 ? (_jsx("div", { style: { padding: "8px 10px", fontSize: 13, color: "var(--text-dim)" }, children: "Nenhuma op\u00E7\u00E3o encontrada." })) : (opcoesFiltradas.map((o, i) => {
                            const marcado = selecionados.includes(o.value);
                            const destacado = temBusca && i === destaque;
                            return (_jsxs("label", { id: `${idPainel}-${o.value}`, role: "option", "aria-selected": marcado, style: {
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "7px 8px",
                                    borderRadius: 8,
                                    cursor: "pointer",
                                    fontSize: 13,
                                    color: "var(--text)",
                                    background: destacado ? "var(--surface)" : "transparent",
                                }, onMouseEnter: () => temBusca && setDestaque(i), children: [_jsx("input", { type: "checkbox", checked: marcado, onChange: () => alternar(o.value) }), o.label] }, o.value));
                        })) })] })) : null] }));
}
//# sourceMappingURL=FiltroPill.js.map